import { gql } from "@apollo/client";

export const LIST_EMPLOYEES = gql`
  query ListEmployees {
    listEmployees {
      items {
        id
        firstName
        lastName
        email
        phone
        address
        position
        department
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_EMPLOYEE = gql`
  query GetEmployee($id: ID!) {
    getEmployee(id: $id) {
      id
      firstName
      lastName
      email
      phone
      address
      position
      department
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_EMPLOYEE = gql`
  mutation CreateEmployee($input: CreateEmployeeInput!) {
    createEmployee(input: $input) {
      id
      firstName
      lastName
      email
      phone
      address
      position
      department
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_EMPLOYEE = gql`
  mutation UpdateEmployee($id: ID!, $input: UpdateEmployeeInput!) {
    updateEmployee(id: $id, input: $input) {
      id
      firstName
      lastName
      email
      phone
      address
      position
      department
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_EMPLOYEE = gql`
  mutation DeleteEmployee($id: ID!) {
    deleteEmployee(id: $id)
  }
`;

export const GET_EMPLOYEE_COUNT = gql`
  query GetEmployeeCount {
    getEmployeeCount
  }
`;

export const GET_FORM_COUNT = gql`
  query GetFormCount {
    getFormCount
  }
`;

export const GET_SUBMISSION_COUNT = gql`
  query GetSubmissionCount {
    getSubmissionCount
  }
`;

export const GET_USER_SUBMISSIONS = gql`
  query GetUserSubmissions($userId: String!, $limit: Int, $nextToken: String) {
    getUserSubmissions(userId: $userId, limit: $limit, nextToken: $nextToken) {
      items {
        submissionId
        formId
        formVersion
        formTitle
        submittedBy
        submittedAt
        status
        data {
          label
          value
        }
        metadata {
          ipAddress
          userAgent
          deviceType
        }
      }
    }
  }
`;

export const GET_USER_SUBMISSION_COUNT = gql`
  query GetUserSubmissionCount($userId: String!) {
    getUserSubmissionCount(userId: $userId)
  }
`;

export const GET_CERTIFICATES_BY_EMPLOYEE = gql`
  query GetCertificatesByEmployee($employeeId: String!) {
    getCertificatesByEmployee(employeeId: $employeeId) {
      id
      employeeId
      name
      fileName
      fileUrl
      issueDate
      expirationDate
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_CERTIFICATION_INFO = gql`
  mutation CreateCertificationInfo(
    $employeeId: String!
    $input: CertificationInput!
  ) {
    createCertificationInfo(employeeId: $employeeId, input: $input) {
      id
      employeeId
      name
      fileName
      fileUrl
      issueDate
      expirationDate
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_CERTIFICATION_INFO = gql`
  mutation DeleteCertificationInfo($employeeId: String!, $id: ID!) {
    deleteCertificationInfo(employeeId: $employeeId, id: $id)
  }
`;

export const LIST_LOCATIONS = gql`
  query ListLocations(
    $limit: Int
    $nextToken: String
    $sortDirection: String
    $filter: LocationFilter
  ) {
    listLocations(
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
      filter: $filter
    ) {
      items {
        id
        name
        address
        city
        state
        zipCode
        country
        phone
        email
        manager
        capacity
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const GET_LOCATION = gql`
  query GetLocation($id: ID!) {
    getLocation(id: $id) {
      id
      name
      address
      city
      state
      zipCode
      country
      phone
      email
      manager
      capacity
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_LOCATION = gql`
  mutation CreateLocation($input: CreateLocationInput!) {
    createLocation(input: $input) {
      id
      name
      address
      city
      state
      zipCode
      country
      phone
      email
      manager
      capacity
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_LOCATION = gql`
  mutation UpdateLocation($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      id
      name
      address
      city
      state
      zipCode
      country
      phone
      email
      manager
      capacity
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_LOCATION = gql`
  mutation DeleteLocation($id: ID!) {
    deleteLocation(id: $id)
  }
`;

export const GET_LOCATION_COUNT = gql`
  query getLocationCount {
    getLocationCount
  }
`;

export const GET_EMPLOYEE_WITH_CERTIFICATES = gql`
  query GetEmployeeWithCertificates($employeeId: String!) {
    getEmployeeWithCertificates(employeeId: $employeeId) {
      employee {
        id
        firstName
        lastName
        email
        phone
        address
        position
        department
        createdAt
        updatedAt
      }
      certifications {
        id
        employeeId
        name
        fileName
        fileUrl
        issueDate
        expirationDate
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_CERTIFICATION_INFO = gql`
  mutation UpdateCertificationInfo(
    $employeeId: String!
    $id: ID!
    $input: CertificationInput!
  ) {
    updateCertificationInfo(employeeId: $employeeId, id: $id, input: $input) {
      id
      employeeId
      name
      fileName
      fileUrl
      issueDate
      expirationDate
      createdAt
      updatedAt
    }
  }
`;

// Presigned URL for file uploads (if needed)
export const GENERATE_PRESIGNED_URL = gql`
  query GeneratePresignedUrl($id: ID!) {
    generatePresignedUrl(id: $id)
  }
`;

export const CREATE_FORM = gql`
  mutation CreateForm($input: FormInput!) {
    createForm(input: $input) {
      pk
      sk
    }
  }
`;

export const UPDATE_FORM = gql`
  mutation UpdateForm($id: ID!, $input: FormInput!) {
    updateForm(id: $id, input: $input) {
      pk
      sk
    }
  }
`;

export const GET_FORM = gql`
  query GetForm($id: ID!) {
    getForm(id: $id) {
      id
      title
      formType
      description
      version
      status
      sections {
        id
        title
        subtitle
        order
        fields {
          type
          label
          required
          options
          validation {
            minLength
            maxLength
            minValue
            maxValue
            pattern
            minDate
            maxDate
            minItems
            maxItems
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const LIST_FORMS = gql`
  query ListForms(
    $limit: Int
    $nextToken: String
    $sortDirection: String
    $filter: FormFilter
  ) {
    listForms(
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
      filter: $filter
    ) {
      items {
        id
        title
        description
        formType
        version
        status
        sections {
          id
          title
          subtitle
          order
          fields {
            type
            label
            required
            options
            validation {
              minLength
              maxLength
              minValue
              maxValue
              pattern
              minDate
              maxDate
              minItems
              maxItems
            }
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export const DELETE_FORM = gql`
  mutation DeleteForm($formId: ID!) {
    deleteForm(formId: $formId)
  }
`;

export const APPROVE_SUBMISSION = gql`
  mutation ApproveSubmission(
    $formId: ID!
    $submissionId: ID!
    $userId: ID!
    $submittedBy: String!
  ) {
    approveSubmission(
      formId: $formId
      submissionId: $submissionId
      userId: $userId
      submittedBy: $submittedBy
    ) {
      pk
      sk
    }
  }
`;

export const LIST_FORM_SUBMISSIONS = gql`
  query ListFormSubmissions(
    $filter: FormSubmissionFilter
    $limit: Int
    $nextToken: String
  ) {
    listFormSubmissions(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        submissionId
        formId
        formVersion
        formTitle
        submittedBy
        submittedAt
        status
        userId
        data {
          label
          value
        }
        metadata {
          ipAddress
          userAgent
          deviceType
        }
      }
      nextToken
    }
  }
`;

export const SUBMIT_FORM = gql`
  mutation SubmitForm(
    $formId: ID!
    $formTitle: String!
    $formType: String!
    $version: Int!
    $input: [FormFieldValueInput!]!
  ) {
    submitForm(
      formId: $formId
      formTitle: $formTitle
      formType: $formType
      version: $version
      input: $input
    ) {
      pk
      sk
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      username
      temporaryPassword
      userStatus
      group
    }
  }
`;

export const CHECK_USER_EXISTS = gql`
  query CheckUserExists($username: String!) {
    checkUserExists(username: $username)
  }
`;

export const ENABLE_USER = gql`
  mutation EnableUser($username: String!) {
    enableUser(username: $username) {
      username
      userStatus
    }
  }
`;

export const DISABLE_USER = gql`
  mutation DisableUser($username: String!) {
    disableUser(username: $username) {
      username
      userStatus
    }
  }
`;

export const ASSIGN_FORM_TO_EMPLOYEES = gql`
  mutation AssignFormToEmployees(
    $formId: ID!
    $formName: String!
    $employeeAssignments: [EmployeeAssignmentInput!]!
    $dueDate: AWSDateTime!
    $message: String
  ) {
    assignFormToEmployees(
      formId: $formId
      formName: $formName
      employeeAssignments: $employeeAssignments
      dueDate: $dueDate
      message: $message
    ) {
      success
      message
      assignedCount
    }
  }
`;

// Add the input type definition
export const ASSIGN_FORM_TO_EMPLOYEES_INPUT = gql`
  input EmployeeAssignmentInput {
    employeeId: ID!
    employeeName: String!
  }
`;

export const MARK_SUBMISSION_AS_COMPLETED = gql`
  mutation MarkSubmissionAsCompleted($submissionId: ID!) {
    markSubmissionAsCompleted(submissionId: $submissionId) {
      id
      status
    }
  }
`;

export const GET_MY_NOTIFICATIONS = gql`
  query GetMyNotifications {
    getMyNotifications {
      items {
        id
        type
        title
        message
        formId
        formName
        submissionId
        approvedBy
        dueDate
        isRead
        createdAt
        username
      }
    }
  }
`;

export const GET_ALL_NOTIFICATIONS = gql`
  query GetAllNotifications {
    getAllNotifications {
      items {
        id
        type
        title
        message
        formId
        formName
        username
        submissionId
        approvedBy
        dueDate
        isRead
        createdAt
      }
      nextToken
    }
  }
`;

export const GET_PENDING_APPROVALS_COUNT = gql`
  query GetPendingApprovalsCount {
    getPendingApprovalsCount
  }
`;

export const GET_PENDING_SUBMISSIONS_COUNT = gql`
  query GetPendingSubmissionsCount {
    getPendingSubmissionsCount
  }
`;

export const GET_ALL_PENDING_SUBMISSIONS_COUNT = gql`
  query GetAllPendingSubmissionsCount {
    getAllPendingSubmissionsCount
  }
`;

export const DISMISS_NOTIFICATION = gql`
  mutation DismissNotification($notificationId: ID!) {
    dismissNotification(notificationId: $notificationId)
  }
`;

export const GET_FORM_SUBMISSION = gql`
  query GetFormSubmission($submissionId: ID!) {
    getFormSubmission(submissionId: $submissionId) {
      items {
        submissionId
        formId
        formVersion
        formTitle
        submittedBy
        submittedAt
        status
        userId
        data {
          label
          value
        }
        metadata {
          ipAddress
          userAgent
          deviceType
        }
      }
      nextToken
    }
  }
`;

export const UPDATE_FORM_SUBMISSION = gql`
  mutation UpdateFormSubmission(
    $formId: ID!
    $submissionId: ID!
    $input: [FormFieldValueInput!]!
  ) {
    updateFormSubmission(
      formId: $formId
      submissionId: $submissionId
      input: $input
    ) {
      submissionId
      formId
      formVersion
      formTitle
      submittedBy
      submittedAt
      status
      userId
      data {
        label
        value
      }
      metadata {
        ipAddress
        userAgent
        deviceType
      }
    }
  }
`;

export const DELETE_FORM_SUBMISSION = gql`
  mutation DeleteSubmission($id: ID!, $formId: ID!) {
    deleteSubmission(id: $id, formId: $formId)
  }
`;

export interface FormKey {
  pk: string;
  sk: string;
}

// Types for TypeScript
export interface Certification {
  id: string;
  employeeId: string;
  name: string;
  fileName?: string;
  fileUrl?: string;
  issueDate: string;
  expirationDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CertificationInput {
  name: string;
  fileName?: string;
  fileUrl?: string;
  issueDate: string;
  expirationDate?: string;
}

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  position: string;
  department: string;
  createdAt: string;
  updatedAt: string;
}

export interface EmployeeWithCertificates {
  employee: Employee;
  certifications: Certification[];
}

export interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  manager: string;
  capacity: number;
  createdAt: string;
  updatedAt: string;
}

// types/form.ts
export interface FormField {
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  validation?: FieldValidation;
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
  minDate?: string;
  maxDate?: string;
  minItems?: number;
  maxItems?: number;
}

export interface FormInput {
  title: string;
  formType: FormType;
  description?: string;
  sections: FormSectionInput[];
  version: number;
}

export interface FormSectionInput {
  id: string;
  title: string;
  subtitle: string;
  order: number;
  fields: FormFieldInput[];
}

export interface FormFieldInput {
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  validation?: FieldValidationInput;
}

export interface FieldValidationInput {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
  minDate?: string;
  maxDate?: string;
  minItems?: number;
  maxItems?: number;
}

export interface Form {
  id: string;
  title: string;
  description?: string;
  version: number;
  status: FormStatus;
  formType: FormType;
  sections: FormSection[];
  createdAt: string;
  updatedAt: string;
}

export interface FormSection {
  id: string;
  title: string;
  subtitle: string;
  order: number;
  fields: FormField[];
}

export enum FormType {
  EMPLOYEE = "EMPLOYEE",
  LOCATION = "LOCATION",
  COMPLIANCE = "COMPLIANCE",
}

export enum FieldType {
  TEXT = "TEXT",
  EMAIL = "EMAIL",
  NUMBER = "NUMBER",
  SELECT = "SELECT",
  MULTISELECT = "MULTISELECT",
  DATE = "DATE",
  BOOLEAN = "BOOLEAN",
  // FILE = "FILE"
}

export const FIELD_TYPES = {
  [FieldType.TEXT]: { label: "Text Input", icon: "📝" },
  [FieldType.EMAIL]: { label: "Email Input", icon: "📧" },
  [FieldType.NUMBER]: { label: "Number Input", icon: "🔢" },
  [FieldType.SELECT]: { label: "Dropdown", icon: "▼" },
  [FieldType.MULTISELECT]: { label: "Multi-Select", icon: "☑️" },
  [FieldType.DATE]: { label: "Date Picker", icon: "📅" },
  [FieldType.BOOLEAN]: { label: "Checkbox", icon: "✓" },
  // FILE: { label: "File Upload", icon: "📁" },
};

export const FORM_TYPES = {
  EMPLOYEE: { label: "Employee", icon: "👤" }, // Simple person
  LOCATION: { label: "Location", icon: "📍" }, // Map pin
  COMPLIANCE: { label: "Compliance", icon: "📋" },
};

export enum FormStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
}

export enum SubmissionStatus {
  DRAFT = "DRAFT",
  SUBMITTED = "SUBMITTED",
  REVIEWED = "REVIEWED",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface FormSubmission {
  submissionId: string;
  formId: string;
  formVersion: number;
  formTitle: string;
  submittedBy: string;
  submittedAt: string;
  status: SubmissionStatus;
  data: FormFieldValue[];
  metadata?: SubmissionMetadata;
  userId: string;
}

export interface FormFieldValue {
  fieldId: string;
  label: string;
  value: string;
}

export interface SubmissionMetadata {
  ipAddress?: string;
  userAgent?: string;
  deviceType?: string;
}

export interface FormField {
  type: FieldType;
  label: string;
  required: boolean;
  options?: string[];
  validation?: FieldValidation;
}

export interface FormSection {
  id: string;
  title: string;
  subtitle: string;
  order: number;
  fields: FormFieldInput[];
}

// Extended FormInput with sections
export interface FormWithSectionsInput {
  title: string;
  formType: FormType;
  description?: string;
  version: number;
  sections: FormSection[];
  status: FormStatus; // Added
}

export interface FieldValidation {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  pattern?: string;
  minDate?: string;
  maxDate?: string;
}

export interface Form {
  id: string;
  title: string;
  description?: string;
  version: number;
  fields: FormField[];
}

// Input type for the mutation
export interface FormFieldValueInput {
  label: string;
  value: string;
}

export interface CreateUserInput {
  email: string;
  firstName: string;
  lastName: string;
  group: string;
  username: string;
  userId: string;
}

export interface CreateUserResponse {
  username: string;
  temporaryPassword: string;
  userStatus: string;
  group: string;
}
